<template>
  <div>

    <table-tools
      :actions="tableActions"
    />

    <v-data-table
      :items="locationProducts"
      :headers="headers"
      :sort-by="sortBy"
      :loading="loadingLps"
    >

      <template v-slot:item.product.name="{ item }">
        {{ item.product ? item.product.name : '(unknown)' }}
        <template v-if="canSeeIds">({{ item.productID }})</template>
      </template>

      <template v-slot:item.product.productType="{ item }">
        {{ item.product ? $t(`enums.product_types.${item.product.productType}`) : $t('misc.unknown_item') }}
      </template>

      <template v-slot:item.product.verticalType="{ item }">
        {{ item.product ? VerticalRegistry.get(item.product.verticalType)?.title ?? item.product.verticalType : $t('misc.unknown_item') }}
      </template>

      <template v-slot:item.channels="{ item }">
        {{ getLocationProductChannels(item) }}
      </template>

      <template v-slot:item.earlyBird="{ item }">
         {{ item.earlyBird ? t('misc.yes') : t('misc.no') }}
      </template>

      <template v-slot:item.inOut="{ item }">
        {{ item.inOut ? t('misc.yes') : t('misc.no') }}
      </template>

      <template v-slot:item.actions="{ item }">

        <ActionGroup
          :actions="getActions(item)"
          @delete-clicked="openDeleteLocationProductModal(item)"
        />

      </template>

    </v-data-table>

  </div>
</template>

<script setup lang="ts">

import { computed, inject, onBeforeMount, ref } from 'vue'
import { useI18n } from 'vue-i18n'

// components
import LocationProductDeleteModal from './LocationProductDeleteModal.vue'
import SyncRatesModal from './SyncRatesModal.vue'

// models
import Location from '../../../../models/Location'
import LocationProduct from '../../../../models/LocationProduct'
import LocationSolution from '../../../../models/LocationSolution'

// misc
// @ts-ignore
import { openDialog } from 'vue3-promise-dialog'
import ChannelRegistry from '../../../../channels/ChannelRegistry'
import VerticalRegistry from '../../../../verticals/VerticalRegistry'
import NotificationService from '../../../../services/NotificationService'
import { useTitle } from '@vueuse/core'
import type { Can } from '../../../../main'
import type { ActionGroupAction } from '../../../../types/misc'
import type { VDataTableHeader } from '../../../../types/vuetify'
import type { ChannelFacility } from '../../../../models/ChannelFacility'

interface Props {
  location: Location,
}

const props = defineProps<Props>()

const { t } = useI18n()
const title = useTitle()
title.value = `${props.location.locationName}: Products` // TODO: i18n

const $can = inject('$can') as Can
const canSeeIds = $can('seeids')
const canWriteLocationProducts = $can('locationProducts.write')

const locationProducts = computed<LocationProduct[]>(() => {
  return LocationProduct.query()
    .with(['product', 'channelFacilities'])
    .where('locationID', props.location.ID)
    .get()
})

const tableActions = computed(() => [
  {
    title: t('actions.sync_rates'),
    icon: '$syncRates',
    click: syncRates,
    disabled: !locationSolution.value || !locationSolution.value.rateManagement,
    hidden: !canWriteLocationProducts,
  },
  {
    title: t('actions.reload'),
    icon: '$reload',
    click: loadLocationProducts,
    busy: loadingLps.value,
  },
  {
    title: t('actions.create_location_product'),
    icon: '$create',
    to: {name: 'locationProductDetail', params: {locationID: props.location.ID, locationProductID: 'new'}},
    hidden: !canWriteLocationProducts,
  },
])

/**
 * Data table configs
 */

const headers: VDataTableHeader[] = []

if (canSeeIds) {
  headers.push({ title: t('titles.id'), key: 'ID' })
}

headers.push({ title: t('titles.product'), key: 'product.name' })
headers.push({ title: t('titles.product_type'), key: 'product.productType' })
headers.push({ title: t('titles.vertical'), key: 'product.verticalType' })
headers.push({ title: t('titles.channels'), key: 'channels', sortable: false })
headers.push({ title: t('titles.early_bird'), key: 'earlyBird'})
headers.push({ title: t('titles.in_out'), key: 'inOut'})
headers.push({ title: '', key: 'actions', sortable: false })

const sortBy = ref([{ key: 'product.name' }])

/**
 * Initialize
 */

onBeforeMount(() => {
  // Make sure that we have locationProducts for this location
  loadLocationProducts()
  loadLocationSolution()
})

const loadingLps = ref(false)

function loadLocationProducts() {

  loadingLps.value = true

  LocationProduct.fetchByLocationID(props.location.ID)
    .catch((error) => {
      console.error(error)
      NotificationService.error(t('alerts.couldnt_load_products'))
    })
    .finally(() => {
      loadingLps.value = false
    })

}

const loadingSolutions = ref(false)

const locationSolution = computed(() => {
  return LocationSolution.query().where('locationID', props.location.ID).first()
})

function loadLocationSolution() {
  
  loadingSolutions.value = true

  LocationSolution.fetchByLocationID(props.location.ID)
    .catch((e) => {
      console.error(e)
      NotificationService.error('Couldn\'t load location solutions!')
    })
    .finally(() => {
      loadingSolutions.value = false
    })

}

/**
 * Modals
 */

function openDeleteLocationProductModal(locationProduct: LocationProduct) {
  openDialog(LocationProductDeleteModal, {
    locationProduct: locationProduct,
  })
}

/**
 * Utility functions
 */

function getLocationProductChannels(locationProduct: LocationProduct): string {

  const channels: string[] = []

  locationProduct.channelFacilities.forEach((channelFacility: ChannelFacility) => {
    if (!channels.includes(channelFacility.channel)) {
      channels.push(
        ChannelRegistry.find(channelFacility.channel)?.titleShort ?? channelFacility.channel
      )
    }
  })

  return channels.length > 0 ? channels.join(', ') : t('misc.none')
  
}

function getActions(locationProduct: LocationProduct): ActionGroupAction[] {

  const actions: ActionGroupAction[] = [
    {
      label: canWriteLocationProducts ? t('actions.edit') : t('actions.view'),
      icon: canWriteLocationProducts ? '$edit' : '$view',
      to: {
        name: 'locationProductDetail',
        params: {
          locationID: props.location.ID,
          locationProductID: locationProduct.ID
        }
      }
    },
  ]

  if (canWriteLocationProducts) {
    actions.push({
      label: t('actions.remove'),
      icon: '$delete',
      event: 'deleteClicked'
    })
  }

  return actions

}

/**
 * Trigger All Rates
 */

function syncRates() {

  openDialog(SyncRatesModal, {
    locationID: props.location.ID,
  })

}

</script>
